import { useEffect, useState } from 'react';
import { resolveStorageUrls } from '@/lib/storage';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { X } from 'lucide-react';

interface MediaItem {
  id: string;
  storage_path: string;
  file_name: string;
  file_type: string;
}

interface MediaGalleryProps {
  media: MediaItem[];
}

export function MediaGallery({ media }: MediaGalleryProps) {
  const [selectedMedia, setSelectedMedia] = useState<{ url: string; isVideo: boolean } | null>(null);
  const [mediaUrls, setMediaUrls] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let active = true;
    const paths = media.map((item) => item.storage_path);
    resolveStorageUrls(paths).then((map) => {
      if (active) setMediaUrls(map);
    });
    return () => {
      active = false;
    };
  }, [media]);

  if (media.length === 0) return null;

  const imageCount = media.length;

  return (
    <>
      <div
        className={`grid gap-2 rounded-lg overflow-hidden ${
          imageCount === 1
            ? 'grid-cols-1'
            : imageCount === 2
            ? 'grid-cols-2'
            : imageCount === 3
            ? 'grid-cols-2'
            : 'grid-cols-2'
        }`}
      >
        {media.slice(0, 4).map((item, index) => {
          const url = mediaUrls.get(item.storage_path);
          const isVideo = item.file_type === 'video' || item.file_type.startsWith('video/');
          const isLastWithMore = index === 3 && imageCount > 4;

          return (
            <div
              key={item.id}
              className={`relative cursor-pointer ${
                imageCount === 3 && index === 0 ? 'row-span-2' : ''
              }`}
              onClick={() => url && setSelectedMedia({ url, isVideo })}
            >
              {url ? (
                isVideo ? (
                  <video
                    src={url}
                    className="w-full h-full object-cover aspect-square hover:opacity-90 transition-opacity"
                    muted
                    preload="metadata"
                  />
                ) : (
                  <img
                    src={url}
                    alt={item.file_name}
                    className="w-full h-full object-cover aspect-square hover:opacity-90 transition-opacity"
                  />
                )
              ) : (
                <div className="w-full h-full aspect-square bg-muted/50 animate-pulse" />
              )}
              {isLastWithMore && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">
                    +{imageCount - 4}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Lightbox */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-0">
          <button
            onClick={() => setSelectedMedia(null)}
            className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          {selectedMedia && (
            selectedMedia.isVideo ? (
              <video
                src={selectedMedia.url}
                controls
                autoPlay
                className="w-full max-h-[90vh] rounded-lg"
              />
            ) : (
              <img
                src={selectedMedia.url}
                alt="Full size"
                className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
